import React, { useEffect, useMemo, useState } from 'react';

import * as changesets from 'json-diff-ts';

import ClauseCard from './CLauseCard';
import ClauseHeader from './ClauseHeader';
import { handleAddEdit } from './Components/ClauseComponent';
import { ClauseComponentInterface } from './interfaces/ClauseComponentInterface';
import { deleteSingleColumn, getPara } from './utils/ClauseTypeUtils';
import Scrollable from '../../../UniversalComponents/Scrollable/scrollable';
import EditFeature from '../EditFeature/Container/cont';

export default function ConfidentialityClause(props: ClauseComponentInterface) {
  const {
    getClauseDataByType,
    clauseData,
    fileId,
    updatedClauseData,
    postClauseDataByType,
    durationList,
    hasData,
  } = props;
  const data = React.useMemo(
    () => clauseData?.confidentiality && clauseData.confidentiality,
    [clauseData?.confidentiality]
  );

  useEffect(() => {
    hasData && getClauseDataByType(fileId, 'confidentiality');
  }, [hasData, fileId]);

  const [showAdd, setShowAdd] = useState(false);
  const [showAddDuration, setShowAddDuration] = useState(false);
  const [showAddNature, setShowAddNature] = useState(false);
  const [showAddException, setShowAddException] = useState(false);
  const [isChildDeleted, setChildDeleted] = React.useState(true);
  const [activeClause, setActiveClause] = useState<{
    type: string;
    index: number;
  } | null>();

  const updates = useMemo(
    () => updatedClauseData?.confidentiality,
    [updatedClauseData]
  );

  const confidentiality = useMemo(() => {
    if (updates?.confidentiality) {
      const sortData = updates.confidentiality.sort(function (a: any, b: any) {
        return a.para_id - b.para_id || a.sentence_id - b.sentence_id;
      });
      return sortData;
    }
    return [];
  }, [updates]);

  const confidentialityDuration = useMemo(() => {
    if (updates?.duration) {
      const sortData = updates.duration.sort(function (a: any, b: any) {
        return (
          a.para_id - b.para_id || a.start_sentence_id - b.start_sentence_id
        );
      });
      sortData.map((data: any) => {
        const index = durationList.findIndex(
          (list: any) => list.durationTypeId === data.duration_type_id
        );
        const typeIndex = durationList.findIndex(
          (list: any) => list.durationName === data.duration_type
        );
        if (index > -1) {
          const type = durationList[index].durationName;
          data.duration_type = type;
        }
        if (!data.duration_type_id && typeIndex > -1) {
          const id = durationList[typeIndex].durationTypeId;
          data.duration_type_id = id;
        }
      });
      return sortData;
    }
    return [];
  }, [updates]);

  const nature = useMemo(() => {
    if (updates?.nature) {
      const sortData = updates.nature.sort(function (a: any, b: any) {
        return (
          a.para_id - b.para_id || a.start_sentence_id - b.start_sentence_id
        );
      });
      return sortData;
    }
    return [];
  }, [updates]);

  const exceptionToConfidentiality = React.useMemo(() => {
    if (updates?.c_except) {
      const sortData = updates.c_except.sort(function (a: any, b: any) {
        return (
          a.para_id - b.para_id || a.start_sentence_id - b.start_sentence_id
        );
      });
      return sortData;
    }
  }, [updates]);

  React.useEffect(() => {
    if (data && data?.edited_content === null && isChildDeleted) {
      let updatedData = {
        ...updates,
        duration: [],
        nature: [],
        c_except: [],
      };
      if (confidentiality && confidentiality.length) {
        confidentialityDuration?.map((parentData: any) => {
          const index = confidentiality.findIndex(
            (item: any) =>
              item.para_id !== parentData.para_id ||
              item.sentence_id !== parentData.sentence_id
          );
          if (index > -1) {
            confidentialityDuration?.splice(index, 1);
          }
        });
        nature?.map((parentData: any) => {
          const index = confidentiality.findIndex(
            (item: any) =>
              item.para_id !== parentData.para_id ||
              item.sentence_id !== parentData.sentence_id
          );
          if (index > -1) {
            nature?.splice(index, 1);
          }
        });
        exceptionToConfidentiality?.map((parentData: any) => {
          const index = confidentiality.findIndex(
            (item: any) =>
              item.para_id !== parentData.para_id ||
              item.sentence_id !== parentData.sentence_id
          );
          if (index > -1) {
            nature?.splice(index, 1);
          }
        });
        setChildDeleted(false);
        updatedData = {
          ...updates,
          duration: confidentialityDuration,
          nature,
          c_except: exceptionToConfidentiality,
        };
      }
      const diff = changesets.diff(data?.raw_content, updatedData, {
        children: '$index',
      });
      if (diff.length > -1) {
        postClauseDataByType?.(fileId, 'confidentiality', diff, updatedData);
        getClauseDataByType(fileId, 'confidentiality');
      }
    }
  }, [
    data,
    updates,
    exceptionToConfidentiality,
    confidentiality,
    confidentialityDuration,
    isChildDeleted,
    nature,
    postClauseDataByType,
    fileId,
    getClauseDataByType,
  ]);

  const deleteClause = React.useCallback(
    (item: any) => {
      const tableData = item?.table?.[0];
      let Confidentiality: any;
      let confidentiality_duration: any;
      let confidentiality_nature: any;
      let exceptionToConfidentiality_data: any;

      if (tableData?.row > -1 && tableData?.column > -1) {
        Confidentiality = deleteSingleColumn(confidentiality, tableData);
        confidentiality_duration = deleteSingleColumn(
          confidentialityDuration,
          tableData
        );
        confidentiality_nature = deleteSingleColumn(nature, tableData);
        exceptionToConfidentiality_data = deleteSingleColumn(
          exceptionToConfidentiality,
          tableData
        );
      } else {
        Confidentiality = confidentiality?.filter(
          (data: any) =>
            data.para_id !== item.para_id ||
            data.sentence_id !== item.sentence_id
        );
        confidentiality_duration = confidentialityDuration?.filter(
          (data: any) =>
            data.para_id !== item.para_id ||
            data.start_sentence_id !== item.sentence_id
        );
        confidentiality_nature = nature?.filter(
          (data: any) =>
            data.para_id !== item.para_id ||
            data.start_sentence_id !== item.sentence_id
        );
        exceptionToConfidentiality_data = exceptionToConfidentiality?.filter(
          (data: any) =>
            data.para_id !== item.para_id ||
            data.sentence_id !== item.sentence_id
        );
      }

      const updatedData = {
        ...updates,
        confidentiality: Confidentiality,
        duration: confidentiality_duration,
        nature: confidentiality_nature,
        c_except: exceptionToConfidentiality_data,
      };
      const diff = changesets.diff(data?.raw_content, updatedData, {
        children: '$index',
      });
      if (diff.length > -1) {
        postClauseDataByType?.(fileId, 'confidentiality', diff, updatedData);
      }
    },
    [
      confidentiality,
      confidentialityDuration,
      nature,
      exceptionToConfidentiality,
      updates,
      data?.raw_content,
      postClauseDataByType,
      fileId,
    ]
  );
  const deleteDuration = React.useCallback(
    (item: any) => {
      const tableData = item?.table?.[0];
      let duration: any;
      if (tableData?.row > -1 && tableData?.column > -1) {
        duration = deleteSingleColumn(confidentialityDuration, tableData);
      } else {
        duration = confidentialityDuration?.filter(
          (data: any) =>
            data.para_id !== item.para_id ||
            data.start_sentence_id !== item.start_sentence_id
        );
      }
      const updatedData = {
        ...updates,
        duration,
      };
      const diff = changesets.diff(data?.raw_content, updatedData, {
        children: '$index',
      });
      if (diff.length > -1) {
        postClauseDataByType?.(fileId, 'confidentiality', diff, updatedData);
      }
    },
    [updates, postClauseDataByType, confidentialityDuration, data, fileId]
  );

  const deleteNature = React.useCallback(
    (item: any) => {
      const tableData = item?.table?.[0];
      let confidentiality_nature: any;

      if (tableData?.row > -1 && tableData?.column > -1) {
        confidentiality_nature = deleteSingleColumn(nature, tableData);
      } else {
        confidentiality_nature = nature?.filter(
          (data: any) =>
            data.para_id !== item.para_id ||
            data.start_sentence_id !== item.start_sentence_id
        );
      }

      const updatedData = {
        ...updates,
        nature: confidentiality_nature,
      };
      const diff = changesets.diff(data?.raw_content, updatedData, {
        children: '$index',
      });
      if (diff.length > -1) {
        postClauseDataByType?.(fileId, 'confidentiality', diff, updatedData);
      }
    },
    [updates, postClauseDataByType, nature, data, fileId]
  );
  const deleteExceptionToConfidentiality = React.useCallback(
    (item: any) => {
      const tableData = item?.table?.[0];
      let exceptionToConfidentiality_data: any;

      if (tableData?.row > -1 && tableData?.column > -1) {
        exceptionToConfidentiality_data = deleteSingleColumn(
          exceptionToConfidentiality,
          tableData
        );
      } else {
        exceptionToConfidentiality_data = exceptionToConfidentiality?.filter(
          (data: any) => data.para_id !== item.para_id
        );
      }

      const updatedData = {
        ...updates,
        c_except: exceptionToConfidentiality_data,
      };
      const diff = changesets.diff(data?.raw_content, updatedData, {
        children: '$index',
      });
      if (diff.length > -1) {
        postClauseDataByType?.(fileId, 'confidentiality', diff, updatedData);
      }
    },
    [updates, postClauseDataByType, exceptionToConfidentiality, data, fileId]
  );

  return (
    <>
      {!showAdd ? (
        <ClauseHeader
          title="Clause Text"
          buttonText="confidentialty"
          onClick={() =>
            handleAddEdit(props, 'Confidentiality', confidentiality, setShowAdd)
          }
        />
      ) : (
        <EditFeature
          fileId={props.fileId}
          toBeEdited="confidentiality"
          savedInsight={confidentiality ? confidentiality : []}
          savedParentClauseDataPoint={confidentiality}
          editOptionSelected={(selected: boolean) => {
            props.editOptionSelected(selected);
          }}
          childInEditId={props.childInEditId}
          onClose={() => {
            setShowAdd(false);
          }}
          clauseDataByType={data}
          updatedClauseDataByType={updates}
          parentClauseType="confidentiality"
        />
      )}
      <Scrollable maxHeight={200}>
        {confidentiality?.map((item: any, index: number) => (
          <ClauseCard
            key={index}
            onClick={() => {
              setActiveClause({ type: 'confidentiality', index });
              props.onClickHighlight(index, item, 'confidentiality');
            }}
            index={index}
            clauseItem={item}
            sentenceData={props.sentenceData}
            isActive={
              activeClause?.type === 'confidentiality' &&
              activeClause?.index === index
            }
            deleteClause={() => deleteClause(item)}
            para={
              item.sentence ||
              getPara(
                props?.sentenceData,
                item.para_id,
                item.sentence_id,
                item.table && item.table[0]?.row,
                item.table && item.table[0]?.column
              )
            }
          />
        ))}
      </Scrollable>

      {!showAddDuration ? (
        <ClauseHeader
          title="Duration"
          buttonText="duration"
          onClick={() =>
            handleAddEdit(
              props,
              'Confidentiality Duration',
              confidentialityDuration,
              setShowAddDuration
            )
          }
        />
      ) : (
        <EditFeature
          fileId={fileId}
          toBeEdited="Confidentiality Duration"
          savedInsight={confidentialityDuration ? confidentialityDuration : []}
          savedParentClauseDataPoint={confidentialityDuration}
          editOptionSelected={(selected: boolean) => {
            props.editOptionSelected(selected);
          }}
          childInEditId={props.childInEditId}
          onClose={() => {
            setShowAddDuration(false);
          }}
          clauseDataByType={data}
          updatedClauseDataByType={updates}
          parentClauseType="confidentiality"
        />
      )}
      {confidentialityDuration &&
        confidentialityDuration.map((durationData: any, index: number) => (
          <ClauseCard
            key={index}
            onClick={() => {
              setActiveClause({ type: 'duration', index });
              props.onClickHighlight(
                index,
                durationData,
                'Confidentiality Duration'
              );
            }}
            index={index}
            clauseItem={durationData}
            sentenceData={props.sentenceData}
            isActive={
              activeClause?.type === 'duration' && activeClause?.index === index
            }
            deleteClause={() => deleteDuration(durationData)}
            phraseLevel
            subTitle={`${durationData.duration_value} ${durationData.duration_type}`}
          />
        ))}

      {!showAddNature ? (
        <ClauseHeader
          title="Nature"
          buttonText="nature"
          onClick={() =>
            handleAddEdit(
              props,
              'Confidentiality Nature',
              nature,
              setShowAddNature
            )
          }
        />
      ) : (
        <EditFeature
          fileId={fileId}
          toBeEdited="Confidentiality Nature"
          savedInsight={nature ? nature : []}
          savedParentClauseDataPoint={nature}
          editOptionSelected={(selected: boolean) => {
            props.editOptionSelected(selected);
          }}
          childInEditId={props.childInEditId}
          onClose={() => {
            setShowAddNature(false);
          }}
          clauseDataByType={data}
          updatedClauseDataByType={updates}
          parentClauseType="confidentiality"
        />
      )}
      <Scrollable maxHeight={200}>
        {nature?.map((natureItem: any, index: number) => (
          <ClauseCard
            key={index}
            onClick={() => {
              setActiveClause({ type: 'nature', index });
              props.onClickHighlight(index, natureItem, 'Nature');
            }}
            index={index}
            clauseItem={natureItem}
            sentenceData={props.sentenceData}
            isActive={
              activeClause?.type === 'nature' && activeClause?.index === index
            }
            deleteClause={() => deleteNature(natureItem)}
            phraseLevel
          />
        ))}
      </Scrollable>

      {!showAddException ? (
        <ClauseHeader
          title="Exception To Confidentiality"
          buttonText="exception"
          onClick={() =>
            handleAddEdit(
              props,
              'ExceptionToConfidentiality',
              exceptionToConfidentiality,
              setShowAddException
            )
          }
        />
      ) : (
        <EditFeature
          fileId={props.fileId}
          toBeEdited="Exception to Confidentiality"
          savedInsight={
            exceptionToConfidentiality ? exceptionToConfidentiality : []
          }
          savedParentClauseDataPoint={exceptionToConfidentiality}
          editOptionSelected={(selected: boolean) => {
            props.editOptionSelected(selected);
          }}
          childInEditId={props.childInEditId}
          onClose={() => {
            setShowAddException(false);
          }}
          clauseDataByType={data}
          updatedClauseDataByType={updates}
          parentClauseType="confidentiality"
        />
      )}
      <Scrollable maxHeight={200}>
        {exceptionToConfidentiality?.map((item: any, index: number) => (
          <ClauseCard
            key={index}
            onClick={() => {
              setActiveClause({ type: 'exception', index });
              props.onClickHighlight(
                index,
                item,
                'Exception to Confidentiality'
              );
            }}
            index={index}
            clauseItem={item}
            sentenceData={props.sentenceData}
            isActive={
              activeClause?.type === 'exception' &&
              activeClause?.index === index
            }
            deleteClause={() => deleteExceptionToConfidentiality(item)}
            para={
              item.sentence ||
              getPara(
                props?.sentenceData,
                item.para_id,
                item.sentence_id,
                item.table && item.table[0]?.row,
                item.table && item.table[0]?.column
              )
            }
          />
        ))}
      </Scrollable>
    </>
  );
}
